import { useCallback, useContext, useMemo, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import type {
  CellEditRequestEvent,
  IRowNode,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import type { AgGridReactProps } from 'ag-grid-react';
import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { WithLocalStorage } from '@/components';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { getRowId } from '@/grid/types';
import { useAccessToken } from '@/services/auth';
import { PrimaryContext } from '@/services/context';
import {
  collectVersions,
  type OnFarmImplantFreezingEditableField,
  type OnFarmImplantFreezingEmbryo,
  type OnFarmImplantFreezingFieldValue,
  onFarmImplantFreezingOptions,
  OnFarmImplantRequestError,
  useAssignOnFarmImplantFreezingCanes,
  useUpdateOnFarmImplantFreezingFields,
} from '@/services/on-farm-implant';
import { useToast } from '@/services/toast';
import { parseWallClock, toWallClockISO } from '@/utils';
import { useContextMenu } from '@/utils/context-menu';

import { ApplyFreezeDataDialog } from './ApplyFreezeDataDialog';
import { AssignStrawNumbersDialog } from './AssignStrawNumbersDialog';
import {
  FREEZING_CELL_EDITABLE_FIELDS,
  useOnFarmFreezingColumnDefs,
} from './columnDefs';
import { CompleteFreezingDialog } from './CompleteFreezingDialog';
import { useFreezingFieldOptions } from './useFreezingFieldOptions';

type OnFarmFreezingTabProps = {
  sessionId: string | null;
  readOnly?: boolean;
};

const EDITABLE_FIELD_SET: ReadonlySet<string> = new Set(
  FREEZING_CELL_EDITABLE_FIELDS,
);

// Puts a grid cell value in the shape the API expects so an edit that doesn't
// change anything (e.g. 4 vs '4') can be skipped.
const toCellValue = (
  field: OnFarmImplantFreezingEditableField,
  value: unknown,
): OnFarmImplantFreezingFieldValue => {
  if (value === null || value === undefined || value === '') return null;
  switch (field) {
    case 'dtPlus':
    case 'crackedZona':
      return value === true || value === 'true';
    case 'strawNumber': {
      const number = Number(value);
      return Number.isFinite(number) ? number : null;
    }
    case 'freezeDate': {
      const date = parseWallClock(String(value).slice(0, 10));
      return date ? toWallClockISO(date) : null;
    }
    default:
      return String(value).trim() || null;
  }
};

export const OnFarmFreezingTab = ({
  sessionId,
  readOnly = false,
}: OnFarmFreezingTabProps) => {
  const token = useAccessToken();
  const { t } = useTranslation();
  const toast = useToast();
  const gridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const { getContextMenuItems } = useContextMenu({ idKey: 'sessionEmbryoId' });
  const gridRef = useRef<AgGridReact>(null);
  const options = useFreezingFieldOptions(sessionId);
  const columnDefs = useOnFarmFreezingColumnDefs({ readOnly, options });

  const [searchText, setSearchText] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [applyOpen, setApplyOpen] = useState(false);
  const [strawRows, setStrawRows] = useState<OnFarmImplantFreezingEmbryo[]>([]);
  const [strawsOpen, setStrawsOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    ...onFarmImplantFreezingOptions(token, { sessionId: sessionId ?? '' }),
    enabled: !!token && !!sessionId,
  });

  const { mutateAsync: updateFields } = useUpdateOnFarmImplantFreezingFields();
  const { mutateAsync: assignCanes, isPending: assigningCanes } =
    useAssignOnFarmImplantFreezingCanes();

  const rows = useMemo(() => data?.items ?? [], [data]);

  const selectedRows = useMemo(() => {
    const ids = new Set(selectedIds);
    return rows.filter((row) => ids.has(row.sessionEmbryoId));
  }, [rows, selectedIds]);

  const incompleteSelected = selectedRows.filter((row) => !row.canComplete);
  const canComplete =
    selectedRows.length > 0 && incompleteSelected.length === 0;

  const showProblems = useCallback(
    (summary: string, error: unknown) => {
      if (error instanceof OnFarmImplantRequestError && error.problems.length) {
        toast.show({
          severity: 'error',
          summary,
          detail: error.problems.join(' '),
        });
        return;
      }
      console.error(summary, error);
      toast.show({ severity: 'error', summary });
    },
    [toast],
  );

  const handleCellEditRequest = useCallback(
    async (event: CellEditRequestEvent) => {
      const row = event.data as OnFarmImplantFreezingEmbryo | undefined;
      const field = event.colDef.field;
      if (!sessionId || !row || !field || !EDITABLE_FIELD_SET.has(field)) {
        return;
      }

      const editable = field as OnFarmImplantFreezingEditableField;
      const previous = toCellValue(editable, event.oldValue);
      const next = toCellValue(editable, event.newValue);
      if (previous === next) return;

      try {
        await updateFields({
          sessionId,
          input: {
            sessionEmbryoIds: [row.sessionEmbryoId],
            changes: { [editable]: { value: next } },
            versions: row.versions,
          },
        });
      } catch (error) {
        showProblems(t('onFarmImplant.onFarmFreezing.saveFailed'), error);
      }
    },
    [sessionId, showProblems, t, updateFields],
  );

  const selectedRowsInGridOrder = useCallback(() => {
    const byId = new Map(
      selectedRows.map((row) => [row.sessionEmbryoId, row] as const),
    );
    const ordered: OnFarmImplantFreezingEmbryo[] = [];
    gridRef.current?.api.forEachNodeAfterFilterAndSort((node) => {
      const id = (node.data as OnFarmImplantFreezingEmbryo | undefined)
        ?.sessionEmbryoId;
      const row = id ? byId.get(id) : undefined;
      if (!row) return;
      ordered.push(row);
      byId.delete(row.sessionEmbryoId);
    });
    return [...ordered, ...byId.values()];
  }, [selectedRows]);

  const handleOpenStraws = useCallback(() => {
    setStrawRows(selectedRowsInGridOrder());
    setStrawsOpen(true);
  }, [selectedRowsInGridOrder]);

  const handleGenerateCanes = useCallback(async () => {
    if (!sessionId || selectedRows.length === 0) return;
    try {
      const result = await assignCanes({
        sessionId,
        input: {
          sessionEmbryoIds: selectedRows.map((row) => row.sessionEmbryoId),
          versions: collectVersions(selectedRows),
        },
      });
      toast.show({
        severity: 'success',
        summary: t('onFarmImplant.onFarmFreezing.canes.success', {
          count: result.items.length,
        }),
      });
    } catch (error) {
      showProblems(t('onFarmImplant.onFarmFreezing.canes.failed'), error);
    }
  }, [assignCanes, selectedRows, sessionId, showProblems, t, toast]);

  const handleSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    setSelectedIds(
      (event.api.getSelectedRows() as OnFarmImplantFreezingEmbryo[]).map(
        (row) => row.sessionEmbryoId,
      ),
    );
  }, []);

  const handleClearSelection = useCallback(() => {
    gridRef.current?.api.deselectAll();
    setSelectedIds([]);
  }, []);

  const isRowSelectable = useCallback(
    (node: IRowNode) => !readOnly && !!node.data,
    [readOnly],
  );

  if (!sessionId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-24 text-gray-400">
        <i className="pi pi-snowflake mb-3 text-4xl" />
        <p className="text-sm">{t('onFarmImplant.onFarmFreezing.noSession')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 py-24 text-sm text-gray-500">
        <i className="pi pi-spin pi-spinner text-sm" />
        {t('onFarmImplant.onFarmFreezing.loading')}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24 text-gray-500">
        <i className="pi pi-exclamation-triangle text-3xl text-amber-500" />
        <p className="text-sm">{t('onFarmImplant.onFarmFreezing.loadError')}</p>
        <Button
          onClick={() => {
            void refetch();
          }}
        >
          {t('onFarmImplant.onFarmFreezing.retry')}
        </Button>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-24 text-gray-400">
        <i className="pi pi-snowflake mb-3 text-4xl" />
        <p className="text-base font-medium">
          {t('onFarmImplant.onFarmFreezing.emptyTitle')}
        </p>
        <p className="mt-1 text-sm">
          {t('onFarmImplant.onFarmFreezing.emptyBody')}
        </p>
      </div>
    );
  }

  const noSelection = selectedRows.length === 0;

  return (
    <div className="flex flex-1 flex-col px-4 py-3">
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <div className="relative w-56">
          <i className="pi pi-search absolute top-1/2 left-2 -translate-y-1/2 text-xs text-gray-400" />
          <Input
            placeholder={t('onFarmImplant.onFarmFreezing.filterPlaceholder')}
            className="h-7 pl-7 text-xs"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
        </div>
        <span className="text-xs text-gray-500">
          {t('onFarmImplant.onFarmFreezing.embryoCount', {
            count: data?.totalRecords ?? rows.length,
          })}
        </span>
        {!readOnly && (
          <>
            <span className="text-xs text-gray-500">
              {t('onFarmImplant.onFarmFreezing.selectedCount', {
                selected: selectedRows.length,
                total: rows.length,
              })}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={noSelection}
              onClick={handleClearSelection}
            >
              {t('onFarmImplant.onFarmFreezing.clearSelection')}
            </Button>
            <Button
              size="sm"
              disabled={noSelection}
              onClick={() => setApplyOpen(true)}
            >
              <Pencil />
              {t('onFarmImplant.onFarmFreezing.apply.button', {
                count: selectedRows.length,
              })}
            </Button>
            <Button size="sm" disabled={noSelection} onClick={handleOpenStraws}>
              {t('onFarmImplant.onFarmFreezing.straws.button')}
            </Button>
            <Button
              size="sm"
              disabled={noSelection || assigningCanes}
              onClick={() => {
                void handleGenerateCanes();
              }}
            >
              {assigningCanes && <i className="pi pi-spin pi-spinner" />}
              {t('onFarmImplant.onFarmFreezing.canes.button')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!canComplete}
              title={
                incompleteSelected.length > 0
                  ? t('onFarmImplant.onFarmFreezing.complete.incompleteHint', {
                      count: incompleteSelected.length,
                    })
                  : undefined
              }
              onClick={() => setCompleteOpen(true)}
            >
              {t('onFarmImplant.onFarmFreezing.complete.button')}
            </Button>
          </>
        )}
      </div>

      <div
        className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-[calc(100vh-13rem)] min-h-[400px] w-full`}
      >
        <WithLocalStorage storageKey="on-farm-implant-on-farm-freezing-grid">
          <AgGridReact
            ref={gridRef}
            gridOptions={gridOptions}
            columnDefs={columnDefs as AgGridReactProps['columnDefs']}
            maintainColumnOrder
            defaultColDef={defaultColDef}
            rowData={rows}
            headerHeight={36}
            rowHeight={40}
            quickFilterText={searchText}
            getRowId={getRowId<OnFarmImplantFreezingEmbryo>(
              (params) => params.data.sessionEmbryoId,
            )}
            getContextMenuItems={getContextMenuItems}
            rowSelection="multiple"
            suppressRowClickSelection
            isRowSelectable={isRowSelectable}
            onSelectionChanged={handleSelectionChanged}
            readOnlyEdit
            onCellEditRequest={handleCellEditRequest}
            stopEditingWhenCellsLoseFocus
            enterNavigatesVertically
            enterNavigatesVerticallyAfterEdit
          />
        </WithLocalStorage>
      </div>

      {!readOnly && (
        <>
          <ApplyFreezeDataDialog
            open={applyOpen}
            onOpenChange={setApplyOpen}
            sessionId={sessionId}
            rows={selectedRows}
            options={options}
          />
          <AssignStrawNumbersDialog
            open={strawsOpen}
            onOpenChange={setStrawsOpen}
            sessionId={sessionId}
            rows={strawRows}
          />
          <CompleteFreezingDialog
            open={completeOpen}
            onOpenChange={setCompleteOpen}
            sessionId={sessionId}
            rows={selectedRows}
            onCompleted={handleClearSelection}
          />
        </>
      )}
    </div>
  );
};
